import { IconName } from 'enums';
import React, {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import ReactTooltip, { TooltipProps } from 'react-tooltip';

import DashboardPanelTooltip from '../../components/DashboardPanelTooltip';
import DashboardPanelTooltipIcon from '../../components/DashboardPanelTooltipIcon';
import {
  IMakeTooltipContext,
  IProviderProps,
  ITooltipContext,
  ITooltipIconInfoProps,
} from './types';

interface MakeTooltipContextProps {
  /**
   * Will apply blue background and white text color styling
   * used on dashboards
   */
  isDashboardTooltip?: boolean;
  /**
   * If a route has multiple Tooltip providers rendered
   * then this must be set, otherwise all tooltips
   * will appear on hover.
   */
  id?: string;
}

/**
 * Creates a tooltip context.
 *
 * To use this just call it.  Wrap your component tree
 * with the TooltipProvider.  And then somewhere in this tree
 * call: useTooltipContext.
 *
 * For a ready made info icon:
 * ...
 *
 * const { TooltipIconInfo } = useTooltipContext();
 *
 * return (
 *  <div>
 *    <TooltipIconInfo text="tooltip text content"
 * </div>
 * )
 *
 * or:
 *
 * Use your own element:
 * ---
 *
 * const { getTooltipProps } = useTooltipContext();
 *
 * return (
 *  <div {...getTooltipProps({ text: 'tooltip text content' })}>Div stuff</div>
 * )
 *
 *
 * If the content of the text prop updates dynamically then do:
 * ---
 *
 * const { TooltipIconInfo, refreshTooltip } = useTooltipContext();
 *
 * useEffect(() => {
 *  refreshTooltip();
 * }, [refreshTooltip, text]);
 *
 * return (...)
 *
 * @returns TooltipContextManager:
 * @param TooltipContextManager.TooltipContext - react context
 * @param TooltipContextManager.TooltipProvider - context provider
 *  @param props  - { children, id, tooltipProps}
 *    @param children - react children
 *    @param id - the id to be passed to the react-tooltip component.  Needed if multiple
 *      instances of the tooltip required on one page (for different styling for instance).
 *    @param tooltipProps - spread props that goes into react-tooltip.  See
 *      there for
 *
 * @param TooltipContextManager.useTooltipContext - context hook that returns
 *  {
 *    TooltipIconInfo: wrapped instance of DashboardPanelTooltipIcon
 *      that takes a "text" prop for the tooltipContent and prefills
 *      the data-for and data-tip props for you.
 *    getTooltipProps: func that takes a "text" prop and returns an
 *      object with the data-for and data-tip values you use to
 *      place on any arbitrary element.
 *    refreshTooltip: function you need to call when the
 *      "text" prop value changes.
 *  }
 */
const makeTooltipContext = ({
  id,
  isDashboardTooltip = true,
}: MakeTooltipContextProps = {}): IMakeTooltipContext => {
  const value = {};
  const TooltipContext = createContext<ITooltipContext>(
    value as ITooltipContext,
  );

  const Tooltip = (props: TooltipProps) => (
    <DashboardPanelTooltip
      id={id}
      {...props}
      isDashboardTooltip={isDashboardTooltip}
    />
  );

  /**
   * If you need to add a tooltip to an element other than the provided
   * Icon then this function will create the props you needs.  Just
   * spread the return object onto your component as props.
   *
   * @param props {
   *    @param text - text to appear in your tooltip.
   * }
   * @returns data-for, data-tip props that connect react-tooltip to
   *  the tooltip portal.
   */
  const getTooltipProps = ({ text }: { text: string }) => ({
    'data-for': id,
    'data-tip': JSON.stringify({ text }),
  });

  const TooltipIconInfo = ({ text, ...rest }: ITooltipIconInfoProps) => (
    <DashboardPanelTooltipIcon
      name={IconName.INFO}
      {...getTooltipProps({ text })}
      {...rest}
    />
  );

  const TooltipDiv = ({ text, ...rest }: ITooltipIconInfoProps) => (
    <div {...getTooltipProps({ text })} {...rest} />
  );

  const TooltipProvider = ({ children, tooltipProps }: IProviderProps) => {
    const newValue = useMemo(
      () => ({
        TooltipIconInfo,
        Tooltip: TooltipDiv,
        refreshTooltip: ReactTooltip.rebuild,
        getTooltipProps,
      }),
      [],
    );

    return (
      <TooltipContext.Provider value={newValue}>
        <Tooltip {...tooltipProps} />
        {children}
      </TooltipContext.Provider>
    );
  };

  const useTooltipContext = (): ITooltipContext => useContext(TooltipContext);

  const MemoTooltipProvider = memo(TooltipProvider);

  return {
    TooltipContext,
    TooltipProvider: MemoTooltipProvider,
    useTooltipContext,
  };
};

export default makeTooltipContext;

export const useRefreshTooltip = (text?: string) => {
  useEffect(() => {
    ReactTooltip.rebuild();
  }, [text]);
};
